import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useServerError} from "../../../../hooks/useServerError";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import {toast} from "react-toastify";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import PostSourceForm from "./PostSourceForm";

const initialState = {
    id: 0,
    name: '',
    postSourceDescription: '',
    isBlocked: false,
    requireManualModeration: false,
    postCategoryId: null,
    postSourceTypeId: null,
    logo: null,
    logoName: '',
    logoSize: null,
    targets: null
}

const PostSource = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [postSource, setPostSource] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setPostSource(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/postSource/getPostSource?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {

                            response.data.cropperSettings = response.data.postCategoryId === 5
                                ? {width: 720, height: 320}
                                : {width: 320, height: 320}

                            setPostSource(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'postSource',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'postSource',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        }
    }

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/postSource/save`, {
                id: postSource.id,
                name: values.name,
                postSourceDescription: values.postSourceDescription,
                isBlocked: values.isBlocked,
                requireManualModeration: values.requireManualModeration,
                postCategoryId: values.postCategoryId,
                postSourceTypeId: values.postSourceTypeId,
                logo: values.image.image,
                logoSize: values.image.size,
                logoName: values.image.name,
                targets: values.targets
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("postSource.form.saved-label"));

                        if (response.data.description) {
                            if (!id || id <= 0) {
                                await load(+response.data.description);
                            }
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'postSource',
                            resultCode: response.data.resultCode,
                            description: response.data.resultCode === ResultCode.ServerError ? null : response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'postSource',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    }

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("postSource.form.form-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <Button buttonType={ButtonType.Default}
                                    form='post-source-form'
                                    type='submit'
                                    disabled={isLoading}>
                                {t("btn.save")}
                            </Button>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <PostSourceForm {...postSource} onSubmit={save}/>
            </div>
        </FormLayout>
    );
};

export default PostSource;
